import {
  RedemptionBy,
  REMDEMPTION_BY_OPTIONS,
  TransactionOrderType,
} from "creso-core/constants";
import {
  useCreateRedemptionTransaction,
  useUpdateRedemptionTransaction,
  useHoldingsData,
  useGetHoldingFolios,
} from "creso-core/hooks";
import {
  calculateCapitalGains,
  convertToCurrency,
  isRedemptionOrder,
} from "creso-core/utils";
import {
  Form,
  FormField,
  V2Loader,
  FormSelect,
  FormItem,
  FormLabel,
  FormMessage,
  FormInput,
  Button,
  FormCheckbox,
  FormCurrencyInput,
} from "web-design-system";
import { useCallback, useContext, useMemo } from "react";
import { toast } from "sonner";
import { useFieldArray, useForm } from "react-hook-form";
import { InfoIcon, XIcon } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { redemptionFormValidationSchema } from "creso-core/zod";
import { APIError } from "web-core/utils";
import {
  HoldingDetails,
  HoldingFoliosDropdown,
  HoldingSchemesDropdown,
} from "web-core";
import { TransactionContext } from "./transaction-wizard";
import FormActions from "./form-actions";
import { handleRedemptionFormValidationError } from "../utils/error";

type FormData = z.infer<typeof redemptionFormValidationSchema>;

const getDefaultFormData = (): FormData => ({
  schemes: [
    {
      scheme: null,
      folio: null,
      redemptionBy: RedemptionBy.UNITS,
      amount: "",
      units: "",
      redeemAllUnits: false,
    },
  ],
});

export default function RedemptionForm() {
  const {
    investment_account_guid = "",
    selectedOrder,
    updateContext,
  } = useContext(TransactionContext);

  const { data: holdingSchemes, isLoading: isHoldingSchemesLoading } =
    useHoldingsData(investment_account_guid);

  const scheme_isin =
    selectedOrder?.type === TransactionOrderType.REDEMPTION
      ? selectedOrder.scheme_isin
      : "";

  const { data: holdingFolios } = useGetHoldingFolios(
    investment_account_guid,
    scheme_isin || "",
    !!investment_account_guid && !!scheme_isin
  );

  const createRedemptionTransaction = useCreateRedemptionTransaction(
    investment_account_guid
  );

  const updateRedemptionTransaction = useUpdateRedemptionTransaction(
    investment_account_guid
  );

  const scheme =
    holdingSchemes?.find((d) => d.scheme_isin === scheme_isin) ?? null;

  const folio = isRedemptionOrder(selectedOrder)
    ? holdingFolios?.find((d) => d.folio_id === selectedOrder.folio_id)
    : null;

  const defaultAmount = useMemo(() => {
    if (selectedOrder?.amount) return selectedOrder.amount;
    if (selectedOrder?.units) return "";
    const folioAmount = Number(folio?.amount);
    return Number.isNaN(folioAmount) ? "" : folioAmount.toFixed(2);
  }, [folio, selectedOrder]);

  const defaultUnits = useMemo(() => {
    if (selectedOrder?.units) return selectedOrder.units;
    if (selectedOrder?.amount) return "";
    const folioUnits = Number(folio?.units);
    return Number.isNaN(folioUnits) ? "" : folioUnits.toFixed(2);
  }, [folio, selectedOrder]);

  const form = useForm({
    resolver: zodResolver(redemptionFormValidationSchema),
    values: isRedemptionOrder(selectedOrder)
      ? {
          schemes: [
            {
              scheme: scheme ?? null,
              folio: folio ?? null,
              redemptionBy: selectedOrder.units
                ? RedemptionBy.UNITS
                : RedemptionBy.AMOUNT,
              amount: defaultAmount,
              units: defaultUnits,
              redeemAllUnits:
                selectedOrder.units === null && selectedOrder.amount === null,
            },
          ],
        }
      : getDefaultFormData(),
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "schemes",
  });

  const handleCreateRedemptionTransaction = useCallback(
    async (values: ReturnType<typeof getDefaultFormData>) => {
      const results = await Promise.allSettled(
        values.schemes.map((item) => {
          return createRedemptionTransaction.mutateAsync({
            investment_account_guid,
            scheme_isin: item.scheme?.scheme_isin as string,
            folio_id: item.folio?.folio_id as number,
            ...(item.redeemAllUnits
              ? {}
              : item.redemptionBy === RedemptionBy.UNITS
                ? {
                    units: item.units,
                  }
                : {
                    amount: item.amount,
                  }),
          });
        })
      );

      return results.map((r, idx) => {
        if (r.status === "fulfilled") {
          return { data: r.value, error: null };
        }
        return {
          data: null,
          error: r.reason,
          isHandled: handleRedemptionFormValidationError(r.reason, form, idx),
        };
      });
    },
    [createRedemptionTransaction, investment_account_guid, form]
  );

  const handleUpdateRedemptionTransaction = useCallback(
    async (values: ReturnType<typeof getDefaultFormData>) => {
      if (
        !selectedOrder?.id ||
        !values.schemes[0].scheme?.scheme_isin ||
        !values.schemes[0].folio
      )
        return {};
      try {
        const result = await updateRedemptionTransaction.mutateAsync({
          id: selectedOrder.id,
          investment_account_guid,
          scheme_isin: values.schemes[0].scheme?.scheme_isin,
          folio_id: values.schemes[0].folio.folio_id,
          ...(values.schemes[0].redeemAllUnits
            ? {}
            : values.schemes[0].redemptionBy === RedemptionBy.UNITS
              ? {
                  units: values.schemes[0].units,
                }
              : {
                  amount: values.schemes[0].amount,
                }),
        });
        return { data: result, error: null };
      } catch (error) {
        return {
          data: null,
          error,
          isHandled: handleRedemptionFormValidationError(error, form, 0),
        };
      }
    },
    [updateRedemptionTransaction, investment_account_guid, selectedOrder, form]
  );

  const onSubmit = useCallback(
    async (values: ReturnType<typeof getDefaultFormData>) => {
      try {
        if (selectedOrder?.id) {
          const result = await handleUpdateRedemptionTransaction(values);
          if (result.error) {
            if (result.isHandled === false) {
              toast.error(`Failed to update order details`, {
                description: (result.error as APIError)?.data?.message,
              });
            }
            return;
          }
        } else {
          const result = await handleCreateRedemptionTransaction(values);
          const failedOrders = result.filter((r) => r.error);

          const unhandledFailedOrders = failedOrders.filter(
            (r) => !r.isHandled
          );

          if (unhandledFailedOrders.length > 0) {
            const description = (unhandledFailedOrders[0].error as APIError)
              ?.data?.message;
            toast.error(`Failed to save order details`, {
              description,
            });
          }

          if (failedOrders.length > 0) {
            return;
          }
        }
        form.reset(getDefaultFormData());
        updateContext?.({
          selectedOrder: null,
        });
      } catch (error) {
        toast.error(`Failed to save order details`, {
          description: (error as Error).message,
        });
      }
    },
    [
      selectedOrder,
      updateContext,
      handleCreateRedemptionTransaction,
      handleUpdateRedemptionTransaction,
      form,
    ]
  );

  const getInfoText = useCallback(
    (index: number) => {
      const selectedFolio = form.watch(`schemes.${index}.folio`);
      if (!selectedFolio) return null;
      const formValues = form.watch(`schemes.${index}`);
      const { redemptionBy, redeemAllUnits } = formValues;
      const result = calculateCapitalGains(
        selectedFolio,
        redeemAllUnits ? RedemptionBy.UNITS : redemptionBy,
        redeemAllUnits
          ? selectedFolio.units
          : redemptionBy === RedemptionBy.AMOUNT
            ? formValues.amount
            : formValues.units
      );
      if (!result) return null;
      return `Approx. LTCG: ${convertToCurrency(result.ltcg)}, STCG: ${convertToCurrency(result.stcg)}`;
    },
    [form]
  );

  return (
    <div className="flex flex-col flex-1">
      <h5 className="mt-1">
        {selectedOrder?.id ? "Update" : "Create"} Redemption order
      </h5>
      <div className="h-px bg-border w-full mt-3 mb-4"></div>
      <V2Loader isLoading={isHoldingSchemesLoading}>
        <Form {...form}>
          <form
            className="flex flex-col flex-1 items-start gap-4 px-1"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {fields.map((field, index) => {
              const infoText = getInfoText(index);

              return (
                <div className="flex w-full gap-1 items-center" key={field.id}>
                  <div className="grid flex-1 grid-cols-2 w-full border-b border-dashed border-border pb-4 gap-x-2 gap-y-4">
                    <FormField
                      className="col-span-1"
                      name={`schemes.${index}.scheme`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Scheme</FormLabel>
                          <HoldingSchemesDropdown
                            value={field.value}
                            onChange={(value) => {
                              field.onChange(value);
                              form.setValue(`schemes.${index}.folio`, null);
                              form.setValue(
                                `schemes.${index}.redeemAllUnits`,
                                false
                              );
                            }}
                            schemefilter={(d) => d.redemption_allowed}
                            investmentAccountGuid={investment_account_guid}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      className="col-span-1"
                      name={`schemes.${index}.folio`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Folio</FormLabel>
                          <HoldingFoliosDropdown
                            value={field.value}
                            onChange={(value) => {
                              field.onChange(value);
                              form.setValue(
                                `schemes.${index}.redeemAllUnits`,
                                false
                              );
                            }}
                            filter={(f) =>
                              f.redemption_allowed &&
                              (Number(f.amount) > 0 || Number(f.units) > 0)
                            }
                            investmentAccountGuid={investment_account_guid}
                            schemeIsin={
                              form.watch(`schemes.${index}.scheme`)?.scheme_isin
                            }
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {form.watch(`schemes.${index}.folio`) && (
                      <HoldingDetails
                        amount={
                          form.watch(`schemes.${index}.folio.amount`) ?? ""
                        }
                        units={form.watch(`schemes.${index}.folio.units`) ?? ""}
                        nav={form.watch(`schemes.${index}.folio.nav`) ?? ""}
                      />
                    )}
                    <FormField
                      className="col-span-1"
                      name={`schemes.${index}.redemptionBy`}
                      render={({ field }) => (
                        <FormSelect
                          placeholder="Select redemption by"
                          label="Redemption by"
                          options={REMDEMPTION_BY_OPTIONS}
                          {...field}
                          onChange={(value) => {
                            field.onChange(value);
                            form.setValue(`schemes.${index}.amount`, "");
                            form.setValue(`schemes.${index}.units`, "");
                            form.setValue(
                              `schemes.${index}.redeemAllUnits`,
                              false
                            );
                          }}
                        />
                      )}
                    />
                    {form.watch(`schemes.${index}.redemptionBy`) ===
                    RedemptionBy.UNITS ? (
                      <FormField
                        key={`schemes.${index}.units`}
                        className="col-span-1"
                        name={`schemes.${index}.units`}
                        render={({ field }) => (
                          <FormInput
                            label="Units"
                            placeholder="Units"
                            {...field}
                            type="number"
                            disabled={
                              !!form.watch(`schemes.${index}.redeemAllUnits`)
                            }
                          />
                        )}
                      />
                    ) : (
                      <FormField
                        key={`schemes.${index}.amount`}
                        className="col-span-1"
                        name={`schemes.${index}.amount`}
                        render={({ field }) => (
                          <FormCurrencyInput
                            label="Amount"
                            placeholder="Amount"
                            {...field}
                            value={field.value}
                            disabled={
                              !!form.watch(`schemes.${index}.redeemAllUnits`)
                            }
                          />
                        )}
                      />
                    )}
                    <FormField
                      name={`schemes.${index}.redeemAllUnits`}
                      render={({ field }) => (
                        <FormCheckbox
                          className="col-span-2"
                          checked={field.value}
                          label="Redeem all units"
                          {...field}
                          onCheckedChange={(value) => {
                            field.onChange(value);
                            if (!value) return;
                            form.setValue(
                              `schemes.${index}.units`,
                              form.watch(`schemes.${index}.folio.units`)
                            );
                            form.setValue(
                              `schemes.${index}.amount`,
                              form.watch(`schemes.${index}.folio.amount`)
                            );
                          }}
                        />
                      )}
                    />
                    {infoText && (
                      <div className="bg-card border border-border col-span-2 flex items-center gap-2 rounded-md p-2">
                        <InfoIcon className="size-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {infoText}
                        </span>
                      </div>
                    )}
                  </div>
                  {fields.length > 1 && (
                    <Button
                      onClick={() => remove(index)}
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="flex-shrink-0"
                    >
                      <XIcon />
                    </Button>
                  )}
                </div>
              );
            })}
            {!selectedOrder?.id && (
              <Button
                onClick={() =>
                  append({
                    scheme: null,
                    folio: null,
                    redemptionBy: RedemptionBy.UNITS,
                    amount: "",
                    units: "",
                    redeemAllUnits: false,
                  })
                }
                type="button"
                variant="link"
                className="text-sm"
              >
                + Add another Scheme
              </Button>
            )}
            <FormActions
              dirty={form.formState.isDirty}
              onReset={() => form.reset()}
              isUpdate={!!selectedOrder?.id}
            />
          </form>
        </Form>
      </V2Loader>
    </div>
  );
}
